import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InventoryPageQueryKeys } from '@Inventory/common/queryKeys';
import { useAppListAccessFlowsV2 } from '@AccessFlows/services/accessFlowsQueries';
import { routes } from '@routes';
import { WITH_SAVED_QUERY_QUERY_PARAM } from '@AccessFlows/common/constants';
import { useListSavedQueries } from '@Inventory/services/savedQueryQueries';

const useSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  return { searchTerm, setSearchTerm };
};

const useModalState = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { savedQueries } = useListSavedQueries();

  const isOpen = Boolean(searchParams.get(InventoryPageQueryKeys.UseInAccessFlow));
  const selectedScopeId = searchParams.get(InventoryPageQueryKeys.SavedQueryId);

  const selectedScope = savedQueries.find((savedQuery) => savedQuery.id === selectedScopeId);

  const handleClose = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(InventoryPageQueryKeys.UseInAccessFlow);
    setSearchParams(newSearchParams);
  };

  return { isOpen, selectedScope, handleClose };
};

const useAccessFlowsData = (searchTerm: string) => {
  const { accessFlows, isFetched } = useAppListAccessFlowsV2();

  const filteredAccessFlows = useMemo(
    () => accessFlows?.filter((flow) => flow.name.toLowerCase().includes(searchTerm.toLowerCase())),
    [accessFlows, searchTerm],
  );

  return { filteredAccessFlows, isFetched };
};

const useNewFlowPath = (selectedScopeId?: string) => {
  return useMemo(
    () => (selectedScopeId ? `${routes.AddAccessFlow.path}?${WITH_SAVED_QUERY_QUERY_PARAM}=${selectedScopeId}` : ''),
    [selectedScopeId],
  );
};

export function useLogic() {
  const { searchTerm, setSearchTerm } = useSearch();
  const { isOpen, selectedScope, handleClose } = useModalState();
  const { filteredAccessFlows, isFetched } = useAccessFlowsData(searchTerm);
  const createNewFlowPath = useNewFlowPath(selectedScope?.id);

  return {
    isOpen,
    selectedScope,
    isFetched,
    filteredAccessFlows,
    searchTerm,
    setSearchTerm,
    handleClose,
    createNewFlowPath,
  };
}
